<template>
  <div class="pa-pkggrid">
    <div
      v-for="pkg in packages"
      :key="pkg.key"
      class="pa-pkg"
      :class="{ 'pa-pkg--popular': pkg.key === 'growth', 'pa-pkg--sel': value === pkg.key, 'pa-pkg--disabled': disabled }"
      role="button"
      :tabindex="disabled ? -1 : 0"
      :aria-pressed="value === pkg.key ? 'true' : 'false'"
      @click="pick(pkg.key)"
      @keydown.enter.prevent="pick(pkg.key)"
      @keydown.space.prevent="pick(pkg.key)"
    >
      <span v-if="pkg.key === 'growth'" class="pa-pkg__pop">MOST POPULAR</span>
      <div class="pa-pkg__head">
        <h4 class="pa-pkg__name">{{ pkg.name }}</h4>
        <span v-if="currentKey === pkg.key" class="pa-status pa-status--green">Current</span>
      </div>
      <div class="pa-pkg__price">${{ money(pkg.price) }}<span>/mo</span></div>
      <div class="pa-pkg__per">per month, cancel anytime</div>
      <div class="pa-pkg__for">{{ pkg.tagline || '' }}</div>
      <ul class="pa-pkg__list">
        <li><i class="material-icons" aria-hidden="true">check</i><b>{{ pkg.silos_per_month }}</b>&nbsp;Top Level page{{ pkg.silos_per_month > 1 ? 's' : '' }} / month</li>
        <li><i class="material-icons" aria-hidden="true">check</i><b>{{ pkg.silos_per_month * pkg.supporting_per_silo }}</b>&nbsp;Supporting pages / month</li>
        <li><i class="material-icons" aria-hidden="true">check</i>Dedicated SEO Manager</li>
        <li v-if="hours(pkg.consult_hours)"><i class="material-icons" aria-hidden="true">check</i>Up to {{ hours(pkg.consult_hours) }} consultation &amp; campaign management</li>
        <li><i class="material-icons" aria-hidden="true">check</i>Publishing</li>
        <li><i class="material-icons" aria-hidden="true">check</i>Performance reporting</li>
        <li>
          <i class="material-icons pa-pkg__verified" aria-hidden="true">verified</i>
          <b>Guaranteed growth in 90 days or we work free</b>&nbsp;
          <a :href="termsUrl" target="_blank" rel="noopener noreferrer" class="pa-pkg__terms" @click.stop>(see terms)</a>
        </li>
      </ul>
    </div>

    <!-- Authority Custom -->
    <div
      class="pa-pkg"
      :class="{ 'pa-pkg--sel': value === 'custom', 'pa-pkg--disabled': disabled }"
      role="button"
      :tabindex="disabled ? -1 : 0"
      :aria-pressed="value === 'custom' ? 'true' : 'false'"
      @click="pick('custom')"
      @keydown.enter.prevent="pick('custom')"
    >
      <div class="pa-pkg__head">
        <h4 class="pa-pkg__name">Authority Custom</h4>
        <span v-if="currentKey === 'custom'" class="pa-status pa-status--green">Current</span>
      </div>
      <div v-if="standardTwin" class="pa-pkg__price pa-pkg__price--muted">${{ money(standardTwin.price) }}<span>/mo</span></div>
      <div v-else class="pa-pkg__price">${{ money(customQuote.price) }}<span>/mo</span></div>
      <div class="pa-pkg__per">per month, cancel anytime</div>
      <div class="pa-pkg__for">{{ customTagline }}</div>

      <div class="pa-pkg__steppers" @click.stop>
        <div class="pa-stepper">
          <span class="pa-stepper__label">Top Level pages / month</span>
          <span class="pa-stepper__ctrl">
            <button type="button" class="pa-btn pa-btn--out pa-stepper__btn" :disabled="disabled || customTl <= limits.min.tl" aria-label="Fewer Top Level pages" @click="setCustom('tl', customTl - 1)"><i class="material-icons">remove</i></button>
            <b class="pa-stepper__value">{{ customTl }}</b>
            <button type="button" class="pa-btn pa-btn--out pa-stepper__btn" :disabled="disabled || customTl >= limits.max.tl" aria-label="More Top Level pages" @click="setCustom('tl', customTl + 1)"><i class="material-icons">add</i></button>
          </span>
        </div>
        <div class="pa-stepper">
          <span class="pa-stepper__label">Supporting pages per Top Level</span>
          <span class="pa-stepper__ctrl">
            <button type="button" class="pa-btn pa-btn--out pa-stepper__btn" :disabled="disabled || customPer <= limits.min.per" aria-label="Fewer supporting pages" @click="setCustom('per', customPer - 1)"><i class="material-icons">remove</i></button>
            <b class="pa-stepper__value">{{ customPer }}</b>
            <button type="button" class="pa-btn pa-btn--out pa-stepper__btn" :disabled="disabled || customPer >= limits.max.per" aria-label="More supporting pages" @click="setCustom('per', customPer + 1)"><i class="material-icons">add</i></button>
          </span>
        </div>
        <div class="pa-pkg__mix">
          {{ customQuote.tl }} Top Level + {{ customQuote.sup }} Supporting pages each month. Each Top Level page comes
          with at least {{ limits.min.per }} Supporting pages.
        </div>
      </div>

      <div v-if="standardTwin" class="pa-pkg__note" @click.stop>
        This mix is already our <b>{{ standardTwin.name }}</b> package.
        <button type="button" class="pa-pkg__note-link" :disabled="disabled" @click="pick(standardTwin.key)">Select {{ shortName(standardTwin) }}</button>
        or change the page mix.
      </div>
      <div v-else-if="cheaperStandard" class="pa-pkg__note" @click.stop>
        <b>{{ cheaperStandard.name }}</b> covers this for ${{ money(cheaperStandard.price) }}/mo.
        <button type="button" class="pa-pkg__note-link" :disabled="disabled" @click="pick(cheaperStandard.key)">Switch</button>
      </div>

      <ul class="pa-pkg__list">
        <li><i class="material-icons" aria-hidden="true">check</i>Dedicated SEO Manager</li>
        <li><i class="material-icons" aria-hidden="true">check</i>Up to {{ hours(customQuote.consultHours) }} consultation &amp; campaign management</li>
        <li><i class="material-icons" aria-hidden="true">check</i>Publishing</li>
        <li><i class="material-icons" aria-hidden="true">check</i>Performance reporting</li>
        <li>
          <i class="material-icons pa-pkg__verified" aria-hidden="true">verified</i>
          <b>Guaranteed growth in 90 days or we work free</b>&nbsp;
          <a :href="termsUrl" target="_blank" rel="noopener noreferrer" class="pa-pkg__terms" @click.stop>(see terms)</a>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import {
  POP_AUTHORITY_CUSTOM_DEFAULTS,
  POP_AUTHORITY_CUSTOM_TAGLINE,
  POP_AUTHORITY_GUARANTEE_TERMS_URL,
  popAuthorityCustomQuote,
  popAuthorityHoursLabel,
  popAuthorityMoney,
  popAuthorityPackageShortName,
} from '@/utils/popAuthority'

/**
 * Package cards (design pa-tabs.jsx PackageChooser, pa.css .pa-pkggrid / .pa-pkg): the three
 * standard packages plus the Authority Custom mix with its live client-side price (the server
 * recomputes on submit). Clicking a card selects it; a custom mix equal to a standard package shows
 * that package's price and a note to select it instead.
 */
export default {
  name: 'PopAuthorityPackageChooser',
  props: {
    /** Selected key: starter | growth | pro | custom */
    value: { type: String, default: 'growth' },
    packages: { type: Array, default: () => [] },
    custom: { type: Object, default: () => POP_AUTHORITY_CUSTOM_DEFAULTS },
    customTl: { type: Number, default: 3 },
    customPer: { type: Number, default: 3 },
    currentKey: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
  },
  data () {
    return {
      customTagline: POP_AUTHORITY_CUSTOM_TAGLINE,
      termsUrl: POP_AUTHORITY_GUARANTEE_TERMS_URL,
    }
  },
  computed: {
    limits () {
      const cfg = this.custom || POP_AUTHORITY_CUSTOM_DEFAULTS
      return { min: cfg.min || POP_AUTHORITY_CUSTOM_DEFAULTS.min, max: cfg.max || POP_AUTHORITY_CUSTOM_DEFAULTS.max }
    },
    customQuote () {
      return popAuthorityCustomQuote(this.customTl, this.customPer, this.custom)
    },
    /** The standard package the custom mix duplicates, if any. */
    standardTwin () {
      const key = this.customQuote.matchesStandardKey
      return key ? (this.packages.find((p) => p.key === key) || null) : null
    },
    /** A standard package that delivers at least the same pages for less. */
    cheaperStandard () {
      const q = this.customQuote
      const candidates = this.packages.filter(
        (p) => p.silos_per_month >= q.tl && p.supporting_per_silo >= q.per && p.price <= q.price
      )
      if (!candidates.length) return null
      return candidates.sort((a, b) => a.price - b.price)[0]
    },
  },
  methods: {
    // package prices can carry cents (Authority Custom mixes land on .50)
    money: popAuthorityMoney,
    hours: popAuthorityHoursLabel,
    shortName: popAuthorityPackageShortName,
    pick (key) {
      if (this.disabled) return
      this.$emit('input', key)
    },
    setCustom (field, value) {
      if (this.disabled) return
      const min = this.limits.min[field]
      const max = this.limits.max[field]
      const v = Math.min(max, Math.max(min, Number(value) || min))
      this.$emit('update:custom-mix', { tl: field === 'tl' ? v : this.customTl, per: field === 'per' ? v : this.customPer })
      this.$emit('input', 'custom')
    },
  },
}
</script>

<style lang="scss" scoped>
@import './setup/setup.scss';

/* design pa.css .pa-pkggrid: four cards, 18px apart; 2 columns under 1280px, 1 under 760px */
.pa-pkggrid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;
  margin: 26px 0 20px;

  @media (max-width: 1280px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 760px) {
    grid-template-columns: minmax(0, 1fr);
  }
}

/* design pa.css .pa-pkg */
.pa-pkg {
  position: relative;
  border: 1.5px solid $pa-border-soft;
  border-radius: 12px;
  background: #fff;
  padding: 20px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 10px;
  color: $pa-teal;
  font-size: 0.9rem;
  line-height: 1.45;
  outline: none;

  &:hover {
    border-color: $pa-border;
  }

  &:focus-visible {
    border-color: $pa-accent;
  }

  &--popular {
    border-color: $pa-accent;
    box-shadow: 0 8px 24px rgba(255, 111, 26, 0.18);
    transform: scale(1.03);

    &:hover {
      border-color: $pa-accent;
    }
  }

  &--sel {
    border-color: $pa-accent;
    background: #fffbf8;
    box-shadow: 0 0 0 1px $pa-accent;

    &:hover {
      border-color: $pa-accent;
    }
  }

  &--popular.pa-pkg--sel {
    box-shadow: 0 8px 24px rgba(255, 111, 26, 0.18), 0 0 0 1px $pa-accent;
  }

  &--disabled {
    cursor: default;
    opacity: 0.7;
  }

  &__pop {
    position: absolute;
    top: -12px;
    left: 50%;
    transform: translateX(-50%);
    background: $pa-accent;
    color: #fff;
    font-size: 0.7rem;
    font-weight: 800;
    border-radius: 999px;
    padding: 5px 14px;
    letter-spacing: 0.06em;
    white-space: nowrap;
    box-shadow: 0 2px 6px rgba(255, 111, 26, 0.35);
    line-height: 1.2;
  }

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  &__name {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 800;
    line-height: 1.45;
    color: $pa-teal;
  }

  &__price {
    font-size: 1.7rem;
    font-weight: 800;
    line-height: 1.45;

    span {
      font-size: 0.95rem;
      font-weight: 700;
      color: $pa-muted;
    }

    &--muted {
      color: $pa-muted;
    }
  }

  &__per {
    font-size: 0.78rem;
    color: $pa-faint;
    margin-top: -6px;
  }

  &__for {
    color: $pa-muted;
    font-size: 0.85rem;
    min-height: 36px;
  }

  &__list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 7px;
    font-size: 0.88rem;

    /* hanging icon: the text flows inline next to it, so a long item wraps under its own first line and
       never under the icon (16px icon + the design's 7px gap) */
    li {
      position: relative;
      padding-left: 23px;
      font-size: 0.88rem;
      line-height: 1.45;
    }

    .material-icons {
      position: absolute;
      left: 0;
      top: 2px;
      color: $pa-success;
      font-size: 16px;
    }
  }

  &__verified {
    color: $pa-accent !important;
  }

  &__terms {
    color: $pa-link;
    font-weight: 600;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  &__steppers {
    display: flex;
    flex-direction: column;
    gap: 8px;
    cursor: default;
  }

  &__mix {
    font-size: 0.8rem;
    color: $pa-muted;
  }

  /* design: "This mix is already our ..." / "... covers this for ..." notes */
  &__note {
    font-size: 0.8rem;
    color: $pa-muted;
    background: #fff3ea;
    border-radius: 8px;
    padding: 8px 10px;
    cursor: default;

    b {
      color: $pa-teal;
    }
  }

  &__note-link {
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    font-weight: 700;
    color: $pa-link;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }

    &:disabled {
      opacity: 0.45;
      cursor: default;
    }
  }
}

/* design Stepper: bordered row, 30px outline buttons, bold value */
.pa-stepper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border: 1px solid $pa-border-soft;
  border-radius: 10px;
  padding: 8px 10px;
  background: #fff;

  &__label {
    font-size: 0.85rem;
    font-weight: 700;
  }

  &__ctrl {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  &__btn {
    width: 30px;
    height: 30px;
    padding: 0;
    min-width: 0;

    .material-icons {
      font-size: 16px;
    }
  }

  &__value {
    width: 28px;
    text-align: center;
    font-size: 1rem;
    font-weight: 800;
  }
}
</style>
